import type { ModalElement } from '@/components/custom-elements/modal'

const DEFAULT_STORAGE_KEY = 'storage-modal-dismissed'

type StorageType = 'local' | 'session'

/**
 * Storage-modal: wraps an x-modal in a <template>. If the storage key is set (dismissed),
 * the inner content is never rendered. Otherwise the template is cloned, appended, and
 * the inner x-modal is shown; when it closes, state is persisted to storage.
 *
 * - data-storage-key: storage key (default: storage-modal-dismissed)
 * - data-storage-type: "local" (localStorage) or "session" (sessionStorage). Default: "local"
 *
 * Markup: put the x-modal (and its content) inside a single <template> child.
 */
export class StorageModalElement extends HTMLElement {
  private observer: MutationObserver | null = null
  private modal: ModalElement | null = null
  private template: HTMLTemplateElement | null = null

  private getStorageKey(): string {
    return this.getAttribute('data-storage-key') || DEFAULT_STORAGE_KEY
  }

  private getStorageType(): StorageType {
    const t = (this.getAttribute('data-storage-type') || 'local').toLowerCase()
    return t === 'session' ? 'session' : 'local'
  }

  private getStorage(): Storage | null {
    if (typeof window === 'undefined') return null
    return this.getStorageType() === 'session' ? window.sessionStorage : window.localStorage
  }

  private isDismissed(): boolean {
    const storage = this.getStorage()
    if (!storage) return false
    return storage.getItem(this.getStorageKey()) === '1'
  }

  private persistDismissed(): void {
    try {
      const storage = this.getStorage()
      if (storage) storage.setItem(this.getStorageKey(), '1')
    } catch {
      // ignore (e.g. private mode)
    }
  }

  private clearRenderedContent(): void {
    const template = this.template
    if (!template) return
    const children = Array.from(this.children).filter((el) => el !== template)
    children.forEach((el) => el.remove())
    this.modal = null
    this.observer?.disconnect()
    this.observer = null
  }

  private observeModal(): void {
    const modal = this.modal
    if (!modal) return
    this.observer = new MutationObserver(() => {
      if (modal.dataset.ui !== 'visible') {
        this.persistDismissed()
        this.hidden = true
        this.clearRenderedContent()
      }
    })
    this.observer.observe(modal, { attributes: true, attributeFilter: ['data-ui'] })
  }

  connectedCallback() {
    this.template = this.querySelector('template')
    if (!this.template) return

    if (this.isDismissed()) {
      this.hidden = true
      return
    }

    const content = this.template.content.cloneNode(true) as DocumentFragment
    this.appendChild(content)

    const modal = this.querySelector<ModalElement>('x-modal')
    this.modal = modal
    if (modal) {
      modal.handleShow()
      this.observeModal()
    }
  }

  disconnectedCallback() {
    this.clearRenderedContent()
    this.template = null
  }
}
